import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { take, catchError, of, finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { NgIf, NgFor, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { Category } from '../../../models/domain/Category';
import { Die } from '../../../models/domain/Die';
import { CategoryService } from '../../../services/category.service';
import { DieService } from '../../../services/die.service';
import { LoadingService } from '../../../services/loading.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { UrlHelper } from '../../../utilities/UrlHelper';

@Component({
    selector: 'app-pdf-download',
    imports: [RouterLink, NgIf, NgFor, CommonModule, FormsModule],
    templateUrl: './pdf-download.component.html',
    styleUrl: './pdf-download.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfDownloadComponent {
    // Filter mode: 'category' or 'die'
    filterMode = signal<'category' | 'die'>('category');

    // Available options
    categories = signal<Category[]>([]);
    dies = signal<Die[]>([]);

    // Selected IDs
    selectedCategoryIds = signal<number[]>([]);
    selectedDieIds = signal<number[]>([]);

    // Search text for filtering lists
    categorySearchText = signal<string>('');
    dieSearchText = signal<string>('');

    // Toggled state
    isToggled = false;

    constructor(
        public themeService: CustomizerSettingsService,
        private categoryService: CategoryService,
        private dieService: DieService,
        private loadingService: LoadingService,
        private toasterService: ToastrService,
        public authService: AuthenticationService
    ) {
        this.initialize();
    }

    ngOnInit(): void {
        this.loadCategories();
        this.loadDies();
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });
    }

    private loadCategories(): void {
        const searchOptions = new BaseSearchOptions(undefined, 'name', 0, 0, 1000);

        this.categoryService.get(searchOptions).pipe(
            take(1),
            catchError((error) => {
                if (error && error.error) {
                    if (error.error.isCustom) {
                        this.toasterService.warning(error.error.message);
                    } else {
                        this.toasterService.error('Failed to load categories');
                    }
                } else {
                    this.toasterService.error('Failed to load categories');
                }
                return of(null);
            })
        ).subscribe(result => {
            if (result && result.body?.values) {
                this.categories.set(result.body.values);
            }
        });
    }

    private loadDies(): void {
        const searchOptions = new BaseSearchOptions(undefined, 'name', 0, 0, 1000);

        this.dieService.get1(searchOptions).pipe(
            take(1),
            catchError((error) => {
                if (error && error.error) {
                    if (error.error.isCustom) {
                        this.toasterService.warning(error.error.message);
                    } else {
                        this.toasterService.error('Failed to load dies');
                    }
                } else {
                    this.toasterService.error('Failed to load dies');
                }
                return of(null);
            })
        ).subscribe(result => {
            if (result && result.body?.values) {
                this.dies.set(result.body.values);
            }
        });
    }

    public setFilterMode(mode: 'category' | 'die'): void {
        this.filterMode.set(mode);
        // Clear selections when switching modes
        this.selectedCategoryIds.set([]);
        this.selectedDieIds.set([]);
    }

    public toggleCategorySelection(categoryId: number): void {
        const currentSelection = this.selectedCategoryIds();
        if (currentSelection.includes(categoryId)) {
            this.selectedCategoryIds.set(currentSelection.filter(id => id !== categoryId));
        } else {
            this.selectedCategoryIds.set([...currentSelection, categoryId]);
        }
    }

    public toggleDieSelection(dieId: number): void {
        const currentSelection = this.selectedDieIds();
        if (currentSelection.includes(dieId)) {
            this.selectedDieIds.set(currentSelection.filter(id => id !== dieId));
        } else {
            this.selectedDieIds.set([...currentSelection, dieId]);
        }
    }

    public isCategorySelected(categoryId: number): boolean {
        return this.selectedCategoryIds().includes(categoryId);
    }

    public isDieSelected(dieId: number): boolean {
        return this.selectedDieIds().includes(dieId);
    }

    public selectAllCategories(): void {
        const filtered = this.getFilteredCategories();
        this.selectedCategoryIds.set(filtered.map(c => c.id!));
    }

    public clearAllCategories(): void {
        this.selectedCategoryIds.set([]);
    }

    public selectAllDies(): void {
        const filtered = this.getFilteredDies();
        this.selectedDieIds.set(filtered.map(d => d.id!));
    }

    public clearAllDies(): void {
        this.selectedDieIds.set([]);
    }

    public getFilteredCategories(): Category[] {
        const searchText = this.categorySearchText().toLowerCase();
        if (!searchText) {
            return this.categories();
        }
        return this.categories().filter(c =>
            c.name.toLowerCase().includes(searchText)
        );
    }

    public getFilteredDies(): Die[] {
        const searchText = this.dieSearchText().toLowerCase();
        if (!searchText) {
            return this.dies();
        }
        return this.dies().filter(d =>
            d.name.toLowerCase().includes(searchText) ||
            d.no.toLowerCase().includes(searchText)
        );
    }

    public generatePDF(): void {
        if (this.filterMode() === 'category') {
            if (this.selectedCategoryIds().length === 0) {
                this.toasterService.warning('Please select at least one category');
                return;
            }
            this.generatePDFByCategories();
        } else {
            if (this.selectedDieIds().length === 0) {
                this.toasterService.warning('Please select at least one die');
                return;
            }
            this.generatePDFByDies();
        }
    }

    private generatePDFByCategories(): void {
        this.loadingService.showLoading();

        this.dieService.generatePdfByCategoryIds(this.selectedCategoryIds()).pipe(
            take(1),
            catchError((error) => {
                if (error && error.error) {
                    if (error.error.isCustom) {
                        this.toasterService.warning(error.error.message);
                    } else {
                        this.toasterService.error('Failed to generate PDF');
                    }
                } else {
                    this.toasterService.error('Failed to generate PDF');
                }
                return of(null);
            }),
            finalize(() => {
                this.loadingService.hideLoading();
            })
        ).subscribe((response) => {
            if (response && response.body) {
                const filename = this.extractFilenameFromResponse(response) || 'dies-by-category.pdf';
                this.downloadBlob(response.body, filename);
                this.toasterService.success('PDF generated successfully');
            }
        });
    }

    private generatePDFByDies(): void {
        this.loadingService.showLoading();

        this.dieService.generatePdfByDieIds(this.selectedDieIds()).pipe(
            take(1),
            catchError((error) => {
                if (error && error.error) {
                    if (error.error.isCustom) {
                        this.toasterService.warning(error.error.message);
                    } else {
                        this.toasterService.error('Failed to generate PDF');
                    }
                } else {
                    this.toasterService.error('Failed to generate PDF');
                }
                return of(null);
            }),
            finalize(() => {
                this.loadingService.hideLoading();
            })
        ).subscribe((response) => {
            if (response && response.body) {
                const filename = this.extractFilenameFromResponse(response) || 'dies-individual.pdf';
                this.downloadBlob(response.body, filename);
                this.toasterService.success('PDF generated successfully');
            }
        });
    }

    private extractFilenameFromResponse(response: any): string | null {
        const contentDisposition = response.headers.get('content-disposition');
        if (!contentDisposition) {
            console.warn('Content-Disposition header not found. Server may need to expose it via Access-Control-Expose-Headers');
            return null;
        }

        // Try to extract filename from content-disposition header
        // Supports both: filename="name.pdf" and filename*=UTF-8''name.pdf
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);

        if (matches && matches[1]) {
            let filename = matches[1].replace(/['"]/g, '');

            // Handle RFC 5987 encoded filenames (filename*=UTF-8''name.pdf)
            if (filename.includes("UTF-8''")) {
                filename = decodeURIComponent(filename.split("UTF-8''")[1]);
            }

            return filename;
        }

        return null;
    }

    private downloadBlob(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    public getImageUrl(path: string): string {
            return UrlHelper.getImageUrl(path);
        }
}
